(() => {
  const MOBILE_QUERY = '(max-width: 767px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const AUTOPLAY_INTERVAL = 6000;
  const SCROLL_SETTLE_DELAY = 120;

  class EventsUpcomingCarousel extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.autoplayTimer = null;
      this.scrollTimer = null;
      this.track = null;
      this.slides = [];
      this.activeIndex = 0;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.pointerPaused = false;
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    }

    connectedCallback() {
      this.abortController?.abort();
      this.stopAutoplay();
      window.clearTimeout(this.scrollTimer);

      this.abortController = new AbortController();
      this.track = this.querySelector('[data-upcoming-carousel-track]');
      this.slides = Array.from(this.querySelectorAll('[data-upcoming-carousel-slide]'));

      // 节点不完整或只有一张卡片时保留静态布局，避免无意义的自动播放。
      if (!this.track || this.slides.length < 2) return;

      const listenerOptions = { signal: this.abortController.signal };
      this.track.addEventListener('scroll', this.handleScroll, listenerOptions);
      this.addEventListener('pointerenter', this.handlePointerEnter, listenerOptions);
      this.addEventListener('pointerleave', this.handlePointerLeave, listenerOptions);
      this.track.addEventListener('pointerdown', this.handlePointerDown, listenerOptions);
      this.track.addEventListener('pointerup', this.handlePointerUp, listenerOptions);
      this.track.addEventListener('pointercancel', this.handlePointerUp, listenerOptions);
      this.addEventListener('focusin', this.handleFocusIn, listenerOptions);
      this.addEventListener('focusout', this.handleFocusOut, listenerOptions);
      this.mobileQuery.addEventListener('change', this.handlePreferenceChange, listenerOptions);
      this.reducedMotionQuery.addEventListener('change', this.handlePreferenceChange, listenerOptions);

      this.applyResponsiveState();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stopAutoplay();
      window.clearTimeout(this.scrollTimer);
    }

    handleScroll = () => {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        this.syncActiveSlide();
        this.restartAutoplay();
      }, SCROLL_SETTLE_DELAY);
    };

    handlePointerEnter = (event) => {
      // 只把真实鼠标悬停视为暂停，避免触屏设备触发兼容性 hover 后无法恢复自动播放。
      if (event.pointerType !== 'mouse') return;
      this.hoverPaused = true;
      this.stopAutoplay();
    };

    handlePointerLeave = (event) => {
      if (event.pointerType !== 'mouse') return;
      this.hoverPaused = false;
      this.pointerPaused = false;
      this.startAutoplay();
    };

    handlePointerDown = () => {
      this.pointerPaused = true;
      this.stopAutoplay();
    };

    handlePointerUp = () => {
      this.pointerPaused = false;
      this.startAutoplay();
    };

    handleFocusIn = () => {
      this.focusPaused = true;
      this.stopAutoplay();
    };

    handleFocusOut = (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.focusPaused = false;
      this.startAutoplay();
    };

    handlePreferenceChange = () => {
      this.applyResponsiveState();
    };

    applyResponsiveState() {
      const mobileCarouselEnabled = this.mobileQuery.matches;

      if (!mobileCarouselEnabled) {
        this.stopAutoplay();
        this.track?.removeAttribute('tabindex');
        this.slides.forEach((slide) => {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        });
        return;
      }

      this.track?.setAttribute('tabindex', '0');
      this.syncActiveSlide();
      this.startAutoplay();
    }

    syncActiveSlide() {
      if (!this.track || !this.mobileQuery.matches) return;

      const trackLeft = this.track.getBoundingClientRect().left;
      let closestDistance = Number.POSITIVE_INFINITY;
      let closestIndex = 0;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.activeIndex = closestIndex;
      this.slides.forEach((slide, index) => {
        const isActive = index === this.activeIndex;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.toggleAttribute('inert', !isActive);
      });
    }

    startAutoplay() {
      if (!this.canAutoplay()) return;

      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => {
        if (this.activeIndex < this.slides.length - 1) {
          this.goToSlide(this.activeIndex + 1);
          return;
        }

        // 普通轮播到达最后一张后停止，不复制卡片，也不跳回第一张。
        this.stopAutoplay();
      }, AUTOPLAY_INTERVAL);
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    stopAutoplay() {
      if (this.autoplayTimer === null) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    canAutoplay() {
      return (
        this.mobileQuery.matches &&
        !this.reducedMotionQuery.matches &&
        !this.hoverPaused &&
        !this.focusPaused &&
        !this.pointerPaused &&
        this.activeIndex < this.slides.length - 1 &&
        this.slides.length > 1
      );
    }

    goToSlide(index) {
      const targetSlide = this.slides[index];
      if (!this.track || !targetSlide || !this.mobileQuery.matches) return;

      this.activeIndex = index;
      const targetOffset =
        this.track.scrollLeft +
        targetSlide.getBoundingClientRect().left -
        this.track.getBoundingClientRect().left;
      this.track.scrollTo({
        left: targetOffset,
        behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
    }
  }

  if (!customElements.get('events-upcoming-carousel')) {
    customElements.define('events-upcoming-carousel', EventsUpcomingCarousel);
  }
})();
